import * as React from 'react';
import { Box } from '@apia/theme';
import { arrayOrArray, convertToArray } from '../../../../utils/util';
import { TApiaMultiplePossibleValue, IApiaMultiple } from '../../../../types';
import MakeField from '../fieldsStore/MakeField';
import useContext from '../../../../hooks/useContext';
import FieldContext from '../fieldsStore/FieldContext';
import IProperty from '../../../../JSApi/constants/IProperty';
import { getFieldErrorStyles } from '../style';
import ApiaMultipleReadonly from './readonly/ApiaMultiple';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import propsStore from '../../../../store/propsStore/propsStore';
import { useApiaFormContext } from '../page/ApiaForm';

export const getSelectOptions = (
  options: TApiaMultiplePossibleValue[] | TApiaMultiplePossibleValue,
): TApiaMultiplePossibleValue[] | [] => {
  return convertToArray(options) || [];
};

const getArrayFromHTMLOptionsValues = (
  options: HTMLOptionsCollection,
): string[] => {
  const values: string[] = [];
  for (let i = 0, l = options.length; i < l; i += 1) {
    if (options[i].selected) {
      values.push(options[i].value.toString());
    }
  }
  return values;
};

export const getSelectedValues = (
  values: TApiaMultiplePossibleValue[] | TApiaMultiplePossibleValue,
): string[] => {
  const optionsArray = getSelectOptions(values);
  const valueArray: string[] = [];
  for (let i = 0; i < optionsArray.length; i++) {
    if (optionsArray[i].selected) {
      valueArray.push(optionsArray[i].value.toString());
    }
  }
  return valueArray;
};

const ApiaMultiple = MakeField(
  React.forwardRef<HTMLSelectElement>((_, ref) => {
    const {
      setValue,
      fireEvent,
      state: { value, isValid },
    } = useContext(FieldContext);
    const { fieldId } = useContext(ApiaComponentContextPrimitive);
    const props = usePropsSelector(fieldId);
    const { field } = props;
    const { attId, attName, possibleValue } = field as IApiaMultiple;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setValue(getArrayFromHTMLOptionsValues(e.target.options));
      fireEvent('onChange');
    };

    const {
      identifier: { className, ...identifier },
      isReadonly,
    } = useApiaFieldCommonProps({ className: 'multiple__field' });

    const { isFormReadOnly } = useApiaFormContext();

    return (
      <Box
        key={`${fieldId}_${attName}`}
        data-required={
          props[IProperty.PROPERTY_READONLY]
            ? props[IProperty.PROPERTY_REQUIRED]
            : null
        }
        sx={React.useMemo(
          () => ({
            display: 'flex',
            margin: '0',
            flexDirection: 'column',
            height: '100%',
          }),
          [],
        )}
        {...identifier}
      >
        {!(props[IProperty.PROPERTY_INPUT_AS_TEXT] || isFormReadOnly) ? (
          <select
            data-variant="forms.selectMultiple"
            sx={{
              variant: 'forms.selectMultiple',
              ...(props[IProperty.PROPERTY_VALUE_COLOR]
                ? { color: props[IProperty.PROPERTY_VALUE_COLOR] }
                : null),
              height: '100%',
              width: [
                props[IProperty.PROPERTY_SIZE]
                  ? `${props[IProperty.PROPERTY_SIZE] as string}px`
                  : '100%',
                '100%',
                props[IProperty.PROPERTY_SIZE]
                  ? `${props[IProperty.PROPERTY_SIZE] as string}px`
                  : '100%',
              ],
              ...getFieldErrorStyles(isValid),
            }}
            multiple
            ref={ref}
            data-field-id={fieldId}
            aria-labelledby={`label_${fieldId}`}
            required={props[IProperty.PROPERTY_REQUIRED]}
            disabled={props[IProperty.PROPERTY_DISABLED]}
            onChange={handleChange}
            value={value as string}
            className={className}
            aria-label={props[IProperty.PROPERTY_ARIA_LABEL]}
            aria-description={props[IProperty.PROPERTY_ARIA_DESCRIPTION]}
            title={
              props[IProperty.PROPERTY_TITLE] ||
              props[IProperty.PROPERTY_TOOLTIP]
            }
            autoFocus={
              props[IProperty.PROPERTY_AUTO_FOCUS] === 'true' ? true : undefined
            }
            autoComplete={props[IProperty.PROPERTY_AUTO_COMPLETE]}
          >
            {getSelectOptions(possibleValue)?.map((opt) => (
              <option
                key={`option_${attId}_${opt?.value}_${opt?.label}`}
                value={opt?.value}
                {...(isReadonly
                  ? {
                      disabled:
                        opt?.value === value ||
                        (!opt?.value && !value) ||
                        (Array.isArray(value) &&
                          ((value.length === 0 && !opt?.value) ||
                            value.includes(opt?.value)))
                          ? undefined
                          : true,
                    }
                  : undefined)}
              >
                {opt?.label}
              </option>
            ))}
          </select>
        ) : (
          arrayOrArray(possibleValue)
            .filter((current) => arrayOrArray(value).includes(current.value))
            .map((current) => current.label)
            .join(', ')
        )}

        {props[IProperty.PROPERTY_INPUT_AS_TEXT] && <ApiaMultipleReadonly />}
      </Box>
    );
  }),
  {
    getInitialValue: ({ context: { fieldId } }) => {
      return getSelectedValues(
        (propsStore.getFieldProps(fieldId).field as IApiaMultiple)
          .possibleValue,
      );
    },
  },
);

export default ApiaMultiple;
